import { useCallback, useEffect, useState } from 'react';
import { CallBackProps, STATUS } from 'react-joyride';

const SEEN_KEY_PREFIX = 'assurex_tour_seen_';

export const TOUR_IDS = {
  SCENARIOS: 'scenarios',
  TEST_CASES: 'test-cases',
  TEST_RUNS: 'test-runs',
  TEST_RUN_EXECUTE: 'test-run-execute',
  FOLDERS: 'folders',
} as const;

export type TourId = typeof TOUR_IDS[keyof typeof TOUR_IDS];

export const ALL_TOUR_IDS: TourId[] = Object.values(TOUR_IDS);

export function isTourSeen(tourId: TourId): boolean {
  return localStorage.getItem(SEEN_KEY_PREFIX + tourId) === '1';
}

export function resetTour(tourId: TourId): void {
  localStorage.removeItem(SEEN_KEY_PREFIX + tourId);
}

export function resetAllTours(): void {
  ALL_TOUR_IDS.forEach(resetTour);
}

/**
 * Drives a single page-level product tour: auto-runs once (per browser) the first
 * time a user lands on the page, and can be replayed on demand via `start()`.
 *
 * `ready` gates the auto-start only — it should be true once every element the tour's
 * steps target actually exists in the DOM (e.g. a folder is selected, a table has rows).
 * If a step's target is missing, react-joyride silently auto-advances past it; when
 * several steps in a row are missing, the whole tour can cascade-skip to the end in one
 * tick, which looks like "Next" does nothing. `ready` prevents auto-start from firing
 * before the page is actually in a state the tour can walk through.
 */
export function useProductTour(tourId: TourId, autoStart: boolean = true, ready: boolean = true) {
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!autoStart || !ready || isTourSeen(tourId)) return;
    const timer = setTimeout(() => setRun(true), 700);
    return () => clearTimeout(timer);
  }, [tourId, autoStart, ready]);

  const start = useCallback(() => setRun(true), []);

  const handleCallback = useCallback((data: CallBackProps) => {
    const { status } = data;
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      localStorage.setItem(SEEN_KEY_PREFIX + tourId, '1');
    }
  }, [tourId]);

  return { run, start, handleCallback };
}
